/**
 * Where the build is, and what this skin says about it.
 *
 * The state machine is the contract's: six `BuildPhase` values advancing on
 * genuine events. What lives here is the voice — a title and a line for the
 * status block — plus the rail's derived states.
 *
 * The rail's LABELS are deliberately not here. They come from
 * `lib/labels.ts`'s `PHASE_LABELS`, the same six words the classic timeline and
 * the hatch pills use, for the reason the hatch skin already writes down: three
 * skins over one pipeline should not name the same five minutes three different
 * ways. Of the vocabularies available, that is the one that says what is
 * actually happening. The charm — such as this skin has any — belongs in the
 * status line, where being specific costs the reader nothing.
 *
 * Pure and total over `BuildPhase`. Typed as a full `Record`, so adding a phase
 * to the contract is a type error here rather than a rail step that silently
 * never lights.
 */

import type { BuildPhase } from "@hatch/shared";
import { BUILD_PHASES, PHASE_ORDER } from "@hatch/shared";
import { PHASE_LABELS } from "../../lib/labels.js";

export type RailState = "done" | "active" | "pending" | "halted";

export interface RailStep {
  /** `01`–`06`. The rail is numbered, which is what makes it read as a spec. */
  ordinal: string;
  label: string;
  state: RailState;
  /** Only ever true for a live active step — see `stageFor`. */
  pulse: boolean;
}

export interface Stage {
  /** Position in `BUILD_PHASES`, which is also the index of the active step. */
  index: number;
  statusTitle: string;
  statusSub: string;
  rail: RailStep[];
}

/**
 * What the status block says, per phase.
 *
 * Declarative and present tense, and specific about which part of the stack is
 * being built — that specificity is the whole register this skin is written in.
 * A visitor at the booth is being shown that a real backend is going up, so the
 * copy names tables and endpoints rather than gesturing at "working…".
 *
 * `done` is the one that has to be careful. The phase reaches `done` when the
 * build finishes and the deploy reports an address, which is BEFORE that address
 * answers. The reveal is what gets to say the site is live; this beat says only
 * that the deploy is out. See the hold copy in BuildView, which withholds even
 * this much.
 */
const STATUS: Record<BuildPhase, { title: string; sub: string }> = {
  queued: {
    title: "Reserving a workspace",
    sub: "Starting ephemeral Xano infrastructure for this build.",
  },
  scaffolding: {
    title: "Planning the build",
    sub: "Working out the tables, endpoints, and pages this one needs.",
  },
  backend: {
    title: "Building the backend",
    sub: "Declaring Postgres tables and wiring the API, in code you will be able to read.",
  },
  frontend: {
    title: "Building the front end",
    sub: "Components, state, and the calls into the API that was just written.",
  },
  deploying: {
    title: "Deploying",
    sub: "Publishing the workspace and putting the site on a real address.",
  },
  done: {
    title: "Deployed",
    sub: "The workspace is published. Waiting for the address to answer.",
  },
};

export function stageFor(phase: BuildPhase, failed: boolean): Stage {
  // Typed as total over `BuildPhase`, but the value crossing the wire is only
  // as trustworthy as the API sending it: a frontend running against a newer
  // API mid-rollout can be handed a phase this build has never heard of. The
  // types say that cannot happen, so nothing would catch it, and the cost of
  // being wrong is a dereference of `undefined` during render — which, with no
  // error boundary above it, is a white page in the middle of someone's build.
  const known = phase in STATUS ? phase : "queued";
  const index = PHASE_ORDER[known];
  const copy = STATUS[known];

  return {
    index,
    statusTitle: copy.title,
    statusSub: copy.sub,
    rail: BUILD_PHASES.map((p, i) => {
      const at = PHASE_ORDER[p];
      const state: RailState =
        at < index ? "done" : at > index ? "pending" : failed ? "halted" : "active";
      return {
        ordinal: String(i + 1).padStart(2, "0"),
        label: PHASE_LABELS[p],
        state,
        pulse: state === "active",
      };
    }),
  };
}
